import React, { useEffect, useState } from 'react';
import classnames from 'classnames';
import { styled } from '@mui/system';
import { theme } from 'mui/theme';
import { useLazyQuery } from '@apollo/client';
import { useRouter } from 'next/router';

import Breadcrumbs from '../../components/Breadcrumbs';
import Header from '../../components/layouts/Header/Header';

import { Metadata } from 'components/Metadata';
import { ORIGIN } from 'src/constants';
import { GET_SITEMAP } from 'apolloClient/queries/sitemap';
import GoogleAnalytics from 'src/ga/grouping';

interface IGroupedSitemaps {
  [key: string]: { url: string; name: string }[];
}
interface ISitemapWithParent {
  url: string;
  name: string;
  parent: string;
}
interface IGetSitemapsResponseItem {
  id: number;
  url: string;
  title: string;
}

const SitemapColumns = styled('div')({
  columnCount: '1',
  [theme.breakpoints.up('md')]: {
    columnCount: '2',
  },
  [theme.breakpoints.up('xl')]: {
    columnCount: '4',
  },
});

const NULL = '{NULL}';
const BLOGS_TITLE = 'Blogs';

const Sitemap: React.FC = () => {
  const { asPath } = useRouter();
  const [getSitemapRequest] = useLazyQuery(GET_SITEMAP);
  const [groupedSitemaps, setGroupedSitemaps] = useState<IGroupedSitemaps>({});
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    (async () => {
      const {
        data: { getSitemaps },
      } = await getSitemapRequest();
      const sitemaps = getSitemaps
        .map((sitemap: IGetSitemapsResponseItem) => {
          const titleComponents = sitemap.title
            ? sitemap.title.split('/')
            : ['', 'Home'];
          return {
            url: sitemap.url,
            parent: titleComponents[2] ? titleComponents[1] : NULL,
            name:
              titleComponents[2] || titleComponents[1] || titleComponents[0],
            old: sitemap,
          };
        })
        .sort((a: ISitemapWithParent, b: ISitemapWithParent) => {
          if (a.name === 'Home') return -1;
          if (b.name === 'Home') return 1;
          if (a.name === 'About us') return -1;
          if (b.name === 'About us') return 1;
          if (a.name === 'Blogs') return -1;
          if (b.name === 'Blogs') return 1;
          if (a.name === 'Terms of use') return -1;
          if (b.name === 'Terms of use') return 1;
          if (a.name === 'Privacy policy') return -1;
          if (b.name === 'Privacy policy') return 1;
          else if (
            (a.parent === NULL && b.name !== 'Home') ||
            a.parent === BLOGS_TITLE ||
            a.url.split('/').length < b.url.split('/').length
          )
            return -1;
          else return 1;
        });

      setGroupedSitemaps(
        sitemaps.reduce((all: IGroupedSitemaps, cur: ISitemapWithParent) => {
          const key = cur.parent;
          if (!all[key]) {
            all[key] = [];
          }
          all[key].push({
            url: cur.url,
            name: cur.name,
          });
          return all;
        }, {})
      );
      setLoading(false);
    })();
  }, [getSitemapRequest]);

  const saveScrollPosition = (): void => {
    localStorage.setItem('scrollPosition', window.scrollY.toString());
  };

  const restoreScrollPosition = (): void => {
    const savedScrollPosition = localStorage.getItem('scrollPosition');
    if (savedScrollPosition) {
      window.scrollTo(0, parseInt(savedScrollPosition, 10));
      localStorage.removeItem('scrollPosition');
    }
  };

  useEffect(() => {
    if (!loading) {
      restoreScrollPosition();
    }
  }, [loading]);

  return (
    <>
      <Metadata
        {...{
          title: `Sitemap | CondoBlackBook`,
          url: `${ORIGIN}${asPath}`,
        }}
      />
      <GoogleAnalytics pageGroup={'Other'} />
      <Header />
      <Breadcrumbs namePage="Site map" />
      <section className="container mb-10">
        <div className="py-12 text-24-32-0.3 uppercase font-sohneBreitBuch">
          Site map
        </div>
        <SitemapColumns>
          {Object.keys(groupedSitemaps).map((parent) => (
            <ul
              key={parent}
              className={classnames('mb-6', {
                hidden: parent === BLOGS_TITLE,
              })}
            >
              {parent !== NULL && (
                <div className="flex items-center mb-2">
                  <div className="w-5 h-0.5 bg-black mr-2" />
                  <span className="font-sohneKraftig">{parent}</span>
                </div>
              )}
              {groupedSitemaps[parent].map(({ url, name }) => (
                <li
                  key={url.toString()}
                  className={classnames({
                    'ml-7': parent !== NULL,
                    hidden: parent === BLOGS_TITLE,
                  })}
                >
                  <a
                    onClick={saveScrollPosition}
                    href={url}
                    className={`flex py-1 ${
                      parent === NULL
                        ? 'items-center font-sohneKraftig'
                        : 'text-14-26-0.3'
                    }`}
                  >
                    {parent === NULL && (
                      <div className="w-5 h-0.5 bg-black mr-2" />
                    )}
                    {name}
                  </a>
                </li>
              ))}
            </ul>
          ))}
        </SitemapColumns>
      </section>
    </>
  );
};

export default Sitemap;
